/**
 * section-product-customizer-2.js
 *
 * Behavior for the product.customizer-2 template's two pop-ups:
 *  1. Color pop-up ("See All Colors") -- clicking a swatch swaps the large
 *     preview image only. Browsing only, no variant selection / add-to-cart.
 *  2. Zakeke customizer pop-up ("Start Customizing") -- lazy-loads Zakeke's
 *     real Shopify integration script the first time the modal opens.
 *
 * Both modals are the theme's existing <modal-dialog> web component
 * (assets/global.js, unmodified) -- this file only adds behavior on top of
 * the open/close it already provides via [data-modal-opener] and the
 * "open" class on .modal.
 *
 * Ported from WristCo (co-customizer2-*), per customizer-2-port-prompt.md.
 * Per the user's explicit note for WBS: use the same mechanism as CO
 * exactly -- pid resolved from product.metafields.custom.custom_product_id,
 * no OAuth, no separate config endpoint. Do one live confirmation fetch
 * against a real WBS product before trusting this, per the port prompt.
 *
 * Zakeke integration mechanism:
 *  - There is no OAuth/token exchange and no separate JSON config endpoint.
 *    Zakeke's Shopify app renders the container, add-to-cart form, and its
 *    own <script> tags (culminating in a `zakekeDesigner({...})` call, with
 *    the per-product config already resolved server-side) directly into
 *    the HTML returned by /apps/zakeke?pid=<product id>. This matches this
 *    repo's own layout/theme.liquid, which already fetches
 *    /products/<handle>?view=zakeke-top and ?view=zakeke-bottom for the
 *    full-page Zakeke flow.
 *  - That fragment lives inside a `.icartShopifyCartContent` wrapper. We
 *    fetch the page, scope everything to that wrapper, and:
 *      a. clone the #zakeke-addtocart form (a plain sibling element, not a
 *         script -- easy to miss, but Zakeke's iframe submits through it)
 *      b. clone any Zakeke <link> stylesheet (link tags apply automatically
 *         once connected to the document, no replay needed)
 *      c. recreate every <script> tag as a real element (scripts parsed via
 *         DOMParser are inert and never auto-execute -- they must be
 *         recreated to run)
 *  - Scoped to `.icartShopifyCartContent` specifically so we don't replay
 *    the rest of that fetched page's scripts (analytics, cart drawer init,
 *    etc.) a second time.
 *  - Loaded once per page view, on first modal open -- not torn down and
 *    reloaded on every open/close, since Zakeke's own script has no
 *    documented teardown API and re-calling zakekeDesigner() on the same
 *    container is unverified. If Zakeke needs a hard reset between opens,
 *    that needs Zakeke's own guidance.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------
   * Color pop-up: preload every swatch's large image once, on first open
   * ------------------------------------------------------------------- */
  /* Clicking a swatch swaps the main image's src directly, which for a
     not-yet-requested image means the browser has to fetch it before
     anything paints -- confirmed live on WRW/WristCo, a visible lag on
     the first click of each color. Preloading every swatch's large-image
     URL as soon as the modal opens means that fetch has already happened
     (or is in flight) by the time a color is actually clicked, so the
     swap reads from cache instead.

     Capped at 4 concurrent requests rather than firing all of them at
     once: with a couple hundred colors on some products, flooding the
     connection queue with that many simultaneous requests makes the swap
     slower, not faster, since whichever image is actually clicked ends up
     stuck in line behind the rest. A small fixed concurrency keeps steady
     background progress without starving the next click. */
  var colorPreloadState = null; /* null | 'done' */
  function preloadColorPreviewImages(modal) {
    if (colorPreloadState === 'done') return;
    colorPreloadState = 'done';

    var sources = [];
    modal.querySelectorAll('[data-color-swatch-btn]').forEach(function (btn) {
      var src = btn.getAttribute('data-large-image');
      if (src) sources.push(src);
    });

    var CONCURRENCY = 4;
    var nextIndex = 0;

    function loadNext() {
      if (nextIndex >= sources.length) return;
      var src = sources[nextIndex++];
      var img = new Image();
      img.onload = img.onerror = loadNext;
      img.src = src;
    }

    for (var i = 0; i < CONCURRENCY; i++) {
      loadNext();
    }
  }

  /* ---------------------------------------------------------------------
   * Color pop-up: category filter buttons (Most Popular / Solid Colors /
   * Segmented / Swirl / Glow / All Colors)
   * ------------------------------------------------------------------- */
  /* Shared by the category-button click handler and the initial
     default-filter application in initColorPopup() below, so there's
     exactly one place that decides which swatches a category shows and
     what happens when the currently-previewed swatch isn't one of them.
     Calling swatchBtn.click() (rather than duplicating the image/label/
     is-active swap here) re-dispatches a real click through the same
     per-swatch listener wired below, so the fallback swatch goes through
     the exact same update path a real click would. Ported from WristCo's
     applyCategoryFilter() (assets/custom-js-customizer-2.js), adapted to
     WBS's data-color-* attribute naming. */
  function applyCategoryFilter(modal, category) {
    var swatches = modal.querySelectorAll('[data-color-swatch-btn]');
    var firstVisible = null;
    var activeStillVisible = false;

    swatches.forEach(function (swatchBtn) {
      var matches =
        category === 'all' ||
        (category === 'most-popular' && swatchBtn.getAttribute('data-filter-popular') === 'true') ||
        swatchBtn.getAttribute('data-filter-category') === category;

      swatchBtn.classList.toggle('is-filtered-out', !matches);

      if (matches) {
        if (!firstVisible) firstVisible = swatchBtn;
        if (swatchBtn.classList.contains('is-active')) activeStillVisible = true;
      }
    });

    if (!activeStillVisible && firstVisible) {
      firstVisible.click();
    }
  }

  /* ---------------------------------------------------------------------
   * Color pop-up: swap large image on swatch click
   * ------------------------------------------------------------------- */
  function initColorPopup() {
    var modal = document.getElementById('ColorPopup2Modal');
    if (!modal) return;

    var mainImage = modal.querySelector('[data-color-popup-main-image]');
    var mainLabel = modal.querySelector('[data-color-popup-main-label]');
    var swatchButtons = modal.querySelectorAll('[data-color-swatch-btn]');

    swatchButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var largeSrc = btn.getAttribute('data-large-image');
        if (!mainImage || !largeSrc) return;

        mainImage.setAttribute('src', largeSrc);
        mainImage.removeAttribute('srcset');
        mainImage.setAttribute('alt', btn.getAttribute('data-alt') || '');

        if (mainLabel) {
          mainLabel.textContent = btn.getAttribute('data-variant-title') || '';
        }

        swatchButtons.forEach(function (other) {
          other.classList.remove('is-active');
        });
        btn.classList.add('is-active');
      });
    });

    var categoriesRow = modal.querySelector('[data-color-categories]');
    var categoryButtons = modal.querySelectorAll('[data-color-category-btn]');

    categoryButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (categoriesRow) {
          categoriesRow.querySelectorAll('[data-color-category-btn]').forEach(function (otherBtn) {
            var isSelected = otherBtn === btn;
            otherBtn.classList.toggle('is-active', isSelected);
            otherBtn.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          });
        }
        applyCategoryFilter(modal, btn.getAttribute('data-category') || 'all');
      });
    });

    /* Applied unconditionally at init (not gated behind first modal open)
       so filtering state already matches the server-rendered default
       is-active button by the time the modal is opened for the first
       time. Mirrors WristCo's init(). */
    var defaultCategoryBtn = modal.querySelector('[data-color-category-btn].is-active');
    if (defaultCategoryBtn) {
      applyCategoryFilter(modal, defaultCategoryBtn.getAttribute('data-category') || 'all');
    }
  }

  /* ---------------------------------------------------------------------
   * Zakeke customizer pop-up: load the real Zakeke fragment on first open
   * ------------------------------------------------------------------- */
  var ZAKEKE_APP_PROXY_PATH = '/apps/zakeke';
  var zakekeLoadState = null; /* null | 'loading' | 'loaded' | 'error' */

  /* Zakeke's stylesheet (portal.zakeke.com/.../customizer.css) is built
     for its normal full-page usage and sets a global `body { overflow:
     hidden }` rule of its own -- confirmed live on WRW by disabling the
     cloned <link> and watching document.body's computed overflow flip
     from hidden back to visible. Since we clone this stylesheet into
     <head> once, if it's never disabled that rule stays active (silently
     breaking page scroll) for the rest of the session after the first
     time the modal was opened. Tracked here so it can be disabled
     specifically when the modal closes (Zakeke still needs its own
     styles while the modal is open, so it's re-enabled on reopen -- not
     just deleted outright). */
  var zakekeStylesheetLinks = [];
  function setZakekeStylesheetEnabled(enabled) {
    zakekeStylesheetLinks.forEach(function (link) {
      link.disabled = !enabled;
    });
  }

  /* Edit Design links from the cart (assets/component-cart-form.js) carry
     design/composition/variant/quantity in the query string instead of
     navigating straight to /apps/zakeke. Stamping those onto
     #zakeke-container before the modal ever opens is what
     buildZakekeFetchUrl() below reads to request the existing design
     instead of a blank one. Ported from WristCo's
     applyZakekeEditParamsFromUrl (assets/custom-js-customizer-2.js),
     adapted to WBS's single #zakeke-container element -- there is no
     separate mount wrapper here the way CO has [data-c2-zakeke-mount]. */
  function applyZakekeEditParamsFromUrl(container) {
    var params = new URLSearchParams(window.location.search);
    var design = params.get('design');
    var composition = params.get('composition');
    var variant = params.get('variant');
    var quantity = params.get('quantity');

    if (design) container.setAttribute('data-c2-design', design);
    if (composition) container.setAttribute('data-c2-composition', composition);
    if (variant) container.setAttribute('data-c2-variant', variant);
    if (quantity) container.setAttribute('data-c2-quantity', quantity);

    return !!(design || composition);
  }

  /* The customizer-2 template is reached via a plain product URL
     (/products/<handle>?view=customizer-2...), so the handle is always the
     first path segment -- no need for a Liquid-rendered data attribute just
     to recover it here. Mirrors WristCo's getProductHandleFromPath(). */
  function getProductHandleFromPath() {
    var match = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    return match ? match[1] : null;
  }

  /* The composition branch mirrors Zakeke's handle+variant route
     (/apps/zakeke/c/<handle>) rather than the pid route, since that's the
     route the "configuration-based edit" cart link has always used.
     Mirrors WristCo's buildZakekeFetchUrl(). */
  function buildZakekeFetchUrl(container, productId) {
    var composition = container.getAttribute('data-c2-composition');
    var design = container.getAttribute('data-c2-design');
    var variant = container.getAttribute('data-c2-variant');
    var quantity = container.getAttribute('data-c2-quantity');

    if (composition) {
      var handle = getProductHandleFromPath();
      if (handle) {
        var compositionUrl = '/apps/zakeke/c/' + handle + '?composition=' + encodeURIComponent(composition);
        if (variant) compositionUrl += '&variant=' + encodeURIComponent(variant);
        return compositionUrl;
      }
    }

    var url = ZAKEKE_APP_PROXY_PATH + '?pid=' + encodeURIComponent(productId);
    if (design) url += '&design=' + encodeURIComponent(design);
    if (quantity) url += '&quantity=' + encodeURIComponent(quantity);
    return url;
  }

  /* Preconnect hints for the three cross-origin hosts a Zakeke modal open
     actually talks to (portal.zakeke.com, api.zakeke.com,
     customizer-worker.zakeke.com) -- fired from the same hover/touchstart
     trigger as the prefetch below so the connection is already warm by the
     time Zakeke's own script needs it. Ported from WristCo's
     ensureZakekePreconnects(). No crossorigin attribute: neither host is
     loaded as a CORS request. */
  var ZAKEKE_PRECONNECT_HOSTS = [
    'https://portal.zakeke.com',
    'https://api.zakeke.com',
    'https://customizer-worker.zakeke.com'
  ];
  var preconnectsAdded = false;
  function ensureZakekePreconnects() {
    if (preconnectsAdded) return;
    preconnectsAdded = true;
    ZAKEKE_PRECONNECT_HOSTS.forEach(function (href) {
      var link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      document.head.appendChild(link);
    });
  }

  /* sessionStorage cache for the raw /apps/zakeke response text, keyed by
     the exact fetch URL (pid plus any design/composition/variant/quantity
     params), so reopening the same product+design later in the same tab
     session skips the network round trip entirely. Wrapped in try/catch:
     sessionStorage can throw in private browsing or once quota is
     exceeded, and failing to cache should never block the actual load.
     Ported from WristCo's read/writeZakekeSessionCache(). */
  var ZAKEKE_SESSION_CACHE_PREFIX = 'zakeke_c2_fragment::';
  function readZakekeSessionCache(fetchUrl) {
    try {
      return sessionStorage.getItem(ZAKEKE_SESSION_CACHE_PREFIX + fetchUrl);
    } catch (err) {
      return null;
    }
  }
  function writeZakekeSessionCache(fetchUrl, html) {
    try {
      sessionStorage.setItem(ZAKEKE_SESSION_CACHE_PREFIX + fetchUrl, html);
    } catch (err) {
      /* Quota exceeded or storage unavailable -- non-fatal, this is a
         pure optimization. */
    }
  }

  /* Fetches and parses the Zakeke fragment ahead of the real click --
     fired on hover (desktop mouseenter) / touchstart (mobile), the instant
     a visitor shows intent, instead of waiting for the click that actually
     opens the modal. Only the fetch + DOMParser work happens here; nothing
     is appended to the live DOM and no <script> runs yet (that stays
     gated behind the real click, in appendZakekeFragment below).

     Memoized on zakekePrefetchPromise: there is exactly one Zakeke
     container per page, so a hover followed by a click reuses the same
     in-flight/resolved promise instead of fetching twice, and a click with
     no prior hover (keyboard activation, or a tap where touchstart and
     click land close together) still works by starting the fetch right
     there in openZakekeCustomizer's own call to this function. Ported from
     WristCo's prefetchZakeke(), adapted to WBS's single #zakeke-container
     element -- there is no separate mount wrapper to pass around. */
  var zakekePrefetchPromise = null;
  function prefetchZakeke(container, productId) {
    if (zakekePrefetchPromise) return zakekePrefetchPromise;

    ensureZakekePreconnects();

    var fetchUrl = buildZakekeFetchUrl(container, productId);
    var cached = readZakekeSessionCache(fetchUrl);
    var htmlPromise = cached !== null
      ? Promise.resolve(cached)
      : fetch(fetchUrl)
          .then(function (response) {
            if (!response.ok) {
              throw new Error('Zakeke request failed with status ' + response.status);
            }
            return response.text();
          })
          .then(function (html) {
            writeZakekeSessionCache(fetchUrl, html);
            return html;
          });

    zakekePrefetchPromise = htmlPromise.then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var scope =
        doc.querySelector('.icartShopifyCartContent') ||
        doc.querySelector('#MainContent') ||
        doc.body;

      if (!scope.querySelector('#zakeke-container')) {
        /* Most likely cause: /apps/zakeke redirected instead of
           returning the customizer (e.g. a customer-tag gate). */
        throw new Error('Zakeke container missing from /apps/zakeke response');
      }

      return {
        addToCartForm: scope.querySelector('#zakeke-addtocart'),
        linkNodes: Array.prototype.slice.call(scope.querySelectorAll('link[href*="zakeke"]')),
        scriptNodes: Array.prototype.slice.call(scope.querySelectorAll('script'))
      };
    });

    return zakekePrefetchPromise;
  }

  /* The real DOM-append + script-execution step -- deliberately kept
     separate from prefetchZakeke() above so it only ever runs after the
     real click, even though the fetch/parse it consumes may have already
     finished well before that click happened. Ported from WristCo's
     appendZakekeFragment(). */
  function appendZakekeFragment(container, fragment) {
    /* Zakeke's own script appends its iframe into #zakeke-container --
       it never clears existing content first -- so a leftover
       "Loading customizer..." placeholder would stay on screen
       permanently, sitting on top of the real customizer once it
       mounted (confirmed live on WRW). */
    container.innerHTML = '';

    if (fragment.addToCartForm && !document.getElementById('zakeke-addtocart')) {
      var clonedForm = fragment.addToCartForm.cloneNode(true);
      if (clonedForm.getAttribute('action') === '/cart/add') {
        clonedForm.setAttribute('action', '/cart/add.js');
      }
      container.insertAdjacentElement('afterend', clonedForm);
    }

    fragment.linkNodes.forEach(function (link) {
      var href = link.getAttribute('href');
      if (href && !document.querySelector('link[href="' + href + '"]')) {
        var cloned = link.cloneNode(true);
        document.head.appendChild(cloned);
        zakekeStylesheetLinks.push(cloned);
      }
    });

    fragment.scriptNodes.forEach(function (oldScript) {
      var newScript = document.createElement('script');
      for (var i = 0; i < oldScript.attributes.length; i++) {
        var attr = oldScript.attributes[i];
        newScript.setAttribute(attr.name, attr.value);
      }
      newScript.textContent = oldScript.textContent;
      document.body.appendChild(newScript);
    });

    zakekeLoadState = 'loaded';
  }

  function openZakekeCustomizer() {
    var container = document.getElementById('zakeke-container');
    if (!container || zakekeLoadState) return;

    var productId = container.getAttribute('data-zakeke-product-id');
    if (!productId) {
      zakekeLoadState = 'error';
      container.innerHTML =
        '<p class="wbs-c2-zakeke-error">This product is not set up for customization yet.</p>';
      return;
    }

    zakekeLoadState = 'loading';

    prefetchZakeke(container, productId)
      .then(function (fragment) {
        appendZakekeFragment(container, fragment);
      })
      .catch(function (err) {
        console.error('Zakeke customizer could not be initialized:', err);
        zakekeLoadState = 'error';
        container.innerHTML =
          '<p class="wbs-c2-zakeke-error">Sorry, the customizer could not be loaded. Please try again.</p>';
      });
  }

  function initZakekePopup() {
    var modalEl = document.getElementById('ZakekeCustomizer2Modal');
    if (!modalEl) return;

    var container = document.getElementById('zakeke-container');
    var productId = container ? container.getAttribute('data-zakeke-product-id') : null;

    /* querySelectorAll, not querySelector: the sticky mobile "Start
       Customizing" bar duplicates this trigger, so there can be more than
       one on the page. openZakekeCustomizer() is itself idempotent
       (guarded by zakekeLoadState), so wiring every match is safe even if
       more than one ends up firing it. */
    document.querySelectorAll('[data-zakeke-trigger]').forEach(function (trigger) {
      /* Hover (desktop) / touchstart (mobile): fire the fetch + parse the
         instant a visitor shows intent, well before the click that
         actually opens the modal. { once: true } per-listener is safe
         even with two triggers (on-page + sticky mobile button) sharing
         one container, since prefetchZakeke() is itself memoized
         globally. Skipped when there's no product id to fetch with --
         openZakekeCustomizer()'s own click handler below still shows the
         "not set up for customization" message in that case. Ported from
         WristCo's hover/touchstart wiring in init(). */
      if (container && productId) {
        trigger.addEventListener('mouseenter', function () {
          prefetchZakeke(container, productId);
        }, { once: true });
        trigger.addEventListener('touchstart', function () {
          prefetchZakeke(container, productId);
        }, { once: true, passive: true });
      }

      trigger.addEventListener('click', openZakekeCustomizer);
    });
  }

  /* ---------------------------------------------------------------------
   * Hide the on-site chat widget while either pop-up is open
   * ------------------------------------------------------------------- */
  /* Toggles a body class rather than hiding matched elements directly, so
     it applies continuously to any current or future matching element --
     no timing dependency on exactly when the chat app's script injects
     its widget relative to a modal opening (confirmed as a real failure
     mode on WRW: the widget's script loaded *after* the modal was already
     open, which a one-time direct-hide-at-open-time missed entirely).
     See section-product-customizer-2-zakeke-popup.liquid for the CSS rule
     this toggles, and why !important is required there, and for which
     chat widget this targets (NEEDS LIVE CONFIRMATION ON WBS -- see that
     file's comment). */
  function setChatWidgetHidden(hidden) {
    document.body.classList.toggle('wbs-c2-hide-chat-widget', hidden);
  }

  /* Tracked separately per modal (not one shared boolean) so that closing
     one modal can't prematurely reveal the widget while the other is
     still open. */
  var chatHiddenBy = { zakeke: false, color: false };
  function recomputeChatVisibility() {
    setChatWidgetHidden(chatHiddenBy.zakeke || chatHiddenBy.color);
  }

  function watchModalOpenState(modalInner, onChange) {
    if (!modalInner) return;
    function sync() {
      onChange(modalInner.classList.contains('open'));
    }
    sync();
    if (window.MutationObserver) {
      var observer = new MutationObserver(sync);
      observer.observe(modalInner, { attributes: true, attributeFilter: ['class'] });
    }
  }

  /* Also triggers the color-modal image preload above -- both need the
     same "did this modal just open" signal, so they share one observer
     per modal instead of registering two. */
  function initModalOpenWatchers() {
    var zakekeModalEl = document.getElementById('ZakekeCustomizer2Modal');
    var zakekeInner = zakekeModalEl ? zakekeModalEl.querySelector('.modal') : null;
    watchModalOpenState(zakekeInner, function (isOpen) {
      /* Belt-and-suspenders alongside the click listeners in
         initZakekePopup(): matches WristCo's own dual-trigger pattern.
         openZakekeCustomizer() is idempotent, so this is a safety net,
         not a second load. */
      if (isOpen) {
        openZakekeCustomizer();
      }
      setZakekeStylesheetEnabled(isOpen);
      chatHiddenBy.zakeke = isOpen;
      recomputeChatVisibility();
    });

    var colorModalEl = document.getElementById('ColorPopup2Modal');
    var colorInner = colorModalEl ? colorModalEl.querySelector('.modal') : null;
    watchModalOpenState(colorInner, function (isOpen) {
      if (isOpen && colorModalEl) {
        preloadColorPreviewImages(colorModalEl);
      }
      chatHiddenBy.color = isOpen;
      recomputeChatVisibility();
    });
  }

  /* ---------------------------------------------------------------------
   * Sticky mobile "Start Customizing" bar
   * ------------------------------------------------------------------- */
  /* Mirrors this theme's own existing sticky-add-to-cart pattern
     (assets/template-product.js's stickyCheckout(): an IntersectionObserver
     on the real on-page button toggling a class on a fixed-position
     duplicate, animated via transform: translateY rather than display) and
     WristCo's co-customizer2-hero.liquid: shows the duplicate fixed-position
     button once the real on-page button scrolls out of view, hides it again
     once scrolled back to it. */
  function initStickyStartButton() {
    var onPageBtn = document.querySelector('[data-c2-start-onpage]');
    var stickyBar = document.querySelector('[data-c2-sticky-start]');
    if (!onPageBtn || !stickyBar || !window.IntersectionObserver) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          stickyBar.classList.remove('active');
        } else {
          stickyBar.classList.add('active');
        }
      });
    });
    observer.observe(onPageBtn);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initColorPopup();
    initZakekePopup();
    initModalOpenWatchers();
    initStickyStartButton();

    var zakekeContainer = document.getElementById('zakeke-container');
    var hasEditParams = zakekeContainer ? applyZakekeEditParamsFromUrl(zakekeContainer) : false;

    /* Edit Design's whole point is landing already open, pre-loaded with
       the existing design -- same modal-dialog .show() the opener/click
       handlers wired above already use, just invoked on page load instead.
       Mirrors WristCo's init(). */
    var zakekeModalDialog = document.getElementById('ZakekeCustomizer2Modal');
    if (hasEditParams && zakekeModalDialog && typeof zakekeModalDialog.show === 'function') {
      zakekeModalDialog.show();
    }
  });
})();
